import { AliasHighlightOverlay } from 'features/knowledgeDocumentViewer/components/viewer/search/AliasHighlightOverlay'
import { DocReferenceOverlay } from 'features/knowledgeDocumentViewer/components/viewer/search/DocReferenceOverlay'
import {
  type SearchHighlightData,
  SearchHighlightOverlay,
} from 'features/knowledgeDocumentViewer/components/viewer/search/SearchHighlightOverlay'
import {
  ActionMode,
  useKnowledgeDocumentUXContextOptional,
} from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import type { AliasHighlight } from 'features/knowledgeDocumentViewer/types/documentSubjects'
import { shouldBoxBeDisabled } from 'features/knowledgeDocumentViewer/utils/bboxHelpers'
import { VIEWER_PREFERENCES_DEFAULTS } from 'features/knowledgeDocumentViewer/utils/viewerPreferencesStorage'
import React, { useCallback } from 'react'

import { OverlaySvg } from './styles'
import { TimeSeriesIndicatorIcon } from './TimeSeriesIndicatorIcon'
import { Box } from './types'

type SelectionBox = {
  x: number
  y: number
  width: number
  height: number
}

type ViewerOverlaySvgProps = {
  actionMode: ActionMode
  boxes: Box[]
  disableBackgroundClick?: boolean
  fillFor: (box: Box) => string
  firstPageWidth?: number
  groupPointerEvents?: React.CSSProperties['pointerEvents']
  hasTimeSeriesData: (label: string | undefined) => boolean
  imageNatural: { w: number; h: number }
  isDragging: boolean
  isMultipage: boolean
  onBackgroundClick?: () => void
  onBoxAuxClick?: (box: Box, event: React.MouseEvent) => void
  onBoxClick?: (box: Box, event: React.MouseEvent) => void
  onBoxContextMenu?: (box: Box, event: React.MouseEvent) => void
  onBoxMouseEnter: (box: Box, event: React.MouseEvent<SVGRectElement>) => void
  onBoxMouseLeave: () => void
  onAliasClick?: (highlight: AliasHighlight) => void
  onAliasHover?: (
    highlight: AliasHighlight | null,
    event?: React.MouseEvent
  ) => void
  onDocReferenceClick?: (highlight: AliasHighlight) => void
  onDocReferenceHover?: (highlight: AliasHighlight | null) => void
  onMouseDown: React.MouseEventHandler<SVGSVGElement>
  onMouseMove: React.MouseEventHandler<SVGSVGElement>
  onMouseUp: React.MouseEventHandler<SVGSVGElement>
  overlayStyle?: React.CSSProperties
  rectPointerEvents?: (
    isDisabled: boolean
  ) => React.CSSProperties['pointerEvents']
  searchHighlights?: SearchHighlightData
  selectionBox: SelectionBox | null
  strokeFor: (box: Box) => string
  svgRef: React.MutableRefObject<SVGSVGElement | null>
}

function ViewerOverlaySvgComponent({
  actionMode,
  boxes,
  disableBackgroundClick = false,
  fillFor,
  firstPageWidth,
  groupPointerEvents,
  hasTimeSeriesData,
  imageNatural,
  isDragging,
  isMultipage,
  onBackgroundClick,
  onBoxAuxClick,
  onBoxClick,
  onBoxContextMenu,
  onBoxMouseEnter,
  onBoxMouseLeave,
  onAliasClick,
  onAliasHover,
  onDocReferenceClick,
  onDocReferenceHover,
  onMouseDown,
  onMouseMove,
  onMouseUp,
  overlayStyle,
  rectPointerEvents = () => 'auto',
  searchHighlights,
  selectionBox,
  strokeFor,
  svgRef,
}: ViewerOverlaySvgProps) {
  // Optional: this overlay also renders outside the UX provider.
  const linkHighlights =
    useKnowledgeDocumentUXContextOptional()?.linkHighlights ??
    VIEWER_PREFERENCES_DEFAULTS.linkHighlights

  const handleSvgRef = useCallback(
    (node: SVGSVGElement | null) => {
      svgRef.current = node
    },
    [svgRef]
  )

  return (
    <OverlaySvg
      ref={handleSvgRef}
      role='presentation'
      style={overlayStyle}
      viewBox={`0 0 ${imageNatural.w} ${imageNatural.h}`}
      preserveAspectRatio='none'
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}>
      <g
        style={
          groupPointerEvents ? { pointerEvents: groupPointerEvents } : undefined
        }>
        <rect
          x={0}
          y={0}
          width={imageNatural.w}
          height={imageNatural.h}
          fill='transparent'
          style={{ pointerEvents: onBackgroundClick ? 'auto' : 'none' }}
          onClick={(event) => {
            if (
              !disableBackgroundClick &&
              event.target === event.currentTarget
            ) {
              onBackgroundClick?.()
            }
          }}
        />

        {isMultipage && typeof firstPageWidth === 'number' && (
          <line
            x1={firstPageWidth}
            y1={0}
            x2={firstPageWidth}
            y2={imageNatural.h}
            stroke='black'
            strokeWidth={2}
            style={{ pointerEvents: 'none' }}
          />
        )}

        {/* Fim highlights (asset aliases + doc references) paint BENEATH the
            OCR boxes: where a box and a highlight cover the same spot, the box
            — the older, richer interaction (select, linked-doc navigation,
            time series) — keeps click priority. A highlight stays interactive
            only where no box sits over it, so its navigation still works for
            references the OCR layer never boxed. */}
        {linkHighlights.asset && (
          <AliasHighlightOverlay
            imageNatural={imageNatural}
            firstPageWidth={firstPageWidth}
            isMultipage={isMultipage}
            onAliasClick={onAliasClick}
            onAliasHover={onAliasHover}
          />
        )}

        {linkHighlights.docReference && (
          <DocReferenceOverlay
            imageNatural={imageNatural}
            firstPageWidth={firstPageWidth}
            isMultipage={isMultipage}
            onDocReferenceClick={onDocReferenceClick}
            onDocReferenceHover={onDocReferenceHover}
          />
        )}

        {boxes.map((box) => {
          const isDisabled = shouldBoxBeDisabled(box, actionMode)
          const expandBy = 2
          const shiftX = 1
          const shiftY = 0
          const showTimeSeriesIndicator = hasTimeSeriesData(box.label)

          return (
            <g key={box.id}>
              <rect
                data-box-label={box.label}
                data-box-type={box.type}
                x={box.x - expandBy + shiftX}
                y={box.y - expandBy + shiftY}
                width={box.w + expandBy * 2}
                height={box.h + expandBy * 2}
                rx={2}
                ry={2}
                fill={fillFor(box)}
                stroke={strokeFor(box)}
                strokeWidth={1.5}
                style={{
                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                  pointerEvents: rectPointerEvents(isDisabled),
                  opacity: isDisabled ? 0.5 : 1,
                }}
                onMouseEnter={(event) => {
                  if (!isDisabled) {
                    onBoxMouseEnter(box, event)
                  }
                }}
                onMouseLeave={onBoxMouseLeave}
                onClick={(event) => {
                  if (!isDisabled) {
                    onBoxClick?.(box, event)
                  }
                }}
                onAuxClick={(event) => {
                  if (!isDisabled) {
                    onBoxAuxClick?.(box, event)
                  }
                }}
                onContextMenu={(event) => {
                  if (!isDisabled) {
                    onBoxContextMenu?.(box, event)
                  }
                }}
              />

              {showTimeSeriesIndicator && (
                <TimeSeriesIndicatorIcon
                  x={box.x + box.w - 6 + shiftX}
                  y={box.y - 10 + shiftY}
                  size={14}
                />
              )}
            </g>
          )
        })}

        {searchHighlights && (
          <SearchHighlightOverlay
            highlights={searchHighlights}
            imageNatural={imageNatural}
            firstPageWidth={firstPageWidth}
            isMultipage={isMultipage}
          />
        )}

        {isDragging && selectionBox && (
          <rect
            x={selectionBox.x}
            y={selectionBox.y}
            width={selectionBox.width}
            height={selectionBox.height}
            fill='rgba(59, 130, 246, 0.1)'
            stroke='rgba(59, 130, 246, 0.8)'
            strokeWidth={2}
            strokeDasharray='5,5'
            style={{ pointerEvents: 'none' }}
          />
        )}
      </g>
    </OverlaySvg>
  )
}

export const ViewerOverlaySvg = React.memo(ViewerOverlaySvgComponent)
